import {
  AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX,
  AUTOMATION_CATEGORY_NAME_MAX_LENGTH,
  type AutomationCategoryResponse,
} from '@reach/shared-types/automation-category';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useAutomationCategories } from '../queries';
import { useCategoryCreator } from '../useCategoryCreator';
import styles from './CategoryChecklist.module.css';
import { CategoryOptionRow } from './CategoryOptionRow';

interface CategoryChecklistProps {
  selectedCategoryIds: string[];
  onSelectedCategoryIdsChange: (categoryIds: string[]) => void;
  onEditCategory: (category: AutomationCategoryResponse) => void;
}

/**
 * Searchable checkbox list of the partner's categories, with inline create. A created category is
 * selected and handed to `onEditCategory`. Used by TemplateCategoryPicker and the templates list row.
 */
export function CategoryChecklist({
  selectedCategoryIds,
  onSelectedCategoryIdsChange,
  onEditCategory,
}: CategoryChecklistProps) {
  const [search, setSearch] = useState('');
  const { data: categories = [] } = useAutomationCategories();
  const { create, isPending } = useCategoryCreator();

  const isAtLimit = selectedCategoryIds.length >= AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX;
  const trimmedSearch = search.trim();
  const normalizedSearch = trimmedSearch.toLowerCase();
  const matchingCategories = categories.filter(category =>
    category.name.toLowerCase().includes(normalizedSearch),
  );
  const searchMatchesExistingName = categories.some(
    category => category.name.toLowerCase() === normalizedSearch,
  );
  const canCreateFromSearch =
    trimmedSearch.length > 0 && !searchMatchesExistingName && !isAtLimit && !isPending;
  const createLabel = `Create “${trimmedSearch}”`;
  const countSummary = `${selectedCategoryIds.length} of ${AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX} categories`;

  const toggleCategory = (categoryId: string) => {
    const categoryIsSelected = selectedCategoryIds.includes(categoryId);
    onSelectedCategoryIdsChange(
      categoryIsSelected
        ? selectedCategoryIds.filter(selectedId => selectedId !== categoryId)
        : [...selectedCategoryIds, categoryId],
    );
  };

  const handleCreate = () =>
    create(trimmedSearch, category => {
      onSelectedCategoryIdsChange([...selectedCategoryIds, category.id]);
      setSearch('');
      onEditCategory(category);
    });

  return (
    <div className={styles.checklist}>
      <input
        type="text"
        value={search}
        maxLength={AUTOMATION_CATEGORY_NAME_MAX_LENGTH}
        placeholder="Search or create a category…"
        aria-label="Search or create a category"
        onChange={event => setSearch(event.target.value)}
      />
      <ul>
        {matchingCategories.map(category => (
          <CategoryOptionRow
            key={category.id}
            category={category}
            isSelected={selectedCategoryIds.includes(category.id)}
            canSelectMore={!isAtLimit}
            onToggle={() => toggleCategory(category.id)}
            onEdit={() => onEditCategory(category)}
          />
        ))}
      </ul>
      {canCreateFromSearch && (
        <button type="button" className={styles.createOption} onClick={handleCreate}>
          <Plus aria-hidden />
          {createLabel}
        </button>
      )}
      <footer>{countSummary}</footer>
    </div>
  );
}
